import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/80 backdrop-blur-md border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="brand-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <span className="h-3 w-3 bg-yellow-400 group-hover:scale-125 transition-transform" />
          <span className="font-display font-semibold tracking-tight text-lg uppercase text-white">
            Fat&nbsp;Head
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="text-sm tracking-widest uppercase text-zinc-400 hover:text-yellow-400 transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta"
            onClick={() => go("#contact")}
            className="bg-yellow-400 text-black hover:bg-yellow-300 px-6 py-3 text-xs uppercase tracking-widest font-bold transition-colors"
          >
            Start Scaling
          </button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="text-left text-lg font-display uppercase tracking-tight text-white hover:text-yellow-400 transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-cta"
                onClick={() => go("#contact")}
                className="mt-2 bg-yellow-400 text-black px-6 py-3 text-xs uppercase tracking-widest font-bold"
              >
                Start Scaling
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
